import { ExcelExport, Finding } from '@consigli/types';
import { TFunction } from 'i18next';

interface ScheduleCheckExportData {
  completeFindings: Finding[];
  partialFindings: Finding[];
  missingFindings: Finding[];
}

/**
 * Creates Excel export structure for schedule-check findings
 * Groups findings into 3 sections: Complete, Partial (Missing in Some), Missing (Missing in Both)
 */
export const createScheduleCheckExcelExport = (
  data: ScheduleCheckExportData,
  t: TFunction,
  projectName: string,
): ExcelExport => {
  const title = `${projectName} - ${t('schedule-check-tabs.comparison')}`;
  const allData: (string | number)[][] = [];

  // Since we have custom sections with their own headers within the data,
  // we use empty dummy headers to prevent the backend from adding an extra header row
  const headers = ['', '', '', ''];

  // Helper function to parse metadata from CUSTOM_MESSAGE
  const parseMetadata = (finding: Finding) => {
    try {
      const customMessage = finding.messageContext?.CUSTOM_MESSAGE;
      if (!customMessage) return null;

      const metadata =
        typeof customMessage === 'string' ? JSON.parse(customMessage) : customMessage;

      return {
        drawingMetadata: metadata?.drawing_metadata,
        schematicMetadata: metadata?.schematic_metadata,
      };
    } catch (e) {
      console.error('Failed to parse CUSTOM_MESSAGE:', e);
      return null;
    }
  };

  // Section 1: Complete Components
  if (data.completeFindings.length > 0) {
    // Section header with empty cells to match column count
    allData.push(['', '', '', '']);
    allData.push([t('schedule-check-comparison.sections.complete'), '', '', '']);
    allData.push(['', '', '', '']);

    // Headers for complete section
    allData.push([
      t('schedule-check.export.reference'),
      t('schedule-check.export.type'),
      t('schedule-check.export.drawings'),
      t('schedule-check.export.schematic'),
    ]);

    data.completeFindings.forEach((finding) => {
      const metadata = parseMetadata(finding);
      const reference = finding.content || '';

      // Get file names from metadata if available
      const drawingFile = metadata?.drawingMetadata?.file_name || t('schedule-check.export.found');
      const schematicFile =
        metadata?.schematicMetadata?.file_name || t('schedule-check.export.found');

      allData.push([
        reference,
        t('schedule-check-comparison.sections.complete'),
        drawingFile,
        schematicFile,
      ]);
    });
  }

  // Section 2: Missing in Some Documents (Partial)
  if (data.partialFindings.length > 0) {
    // Section header with empty cells to match column count
    allData.push(['', '', '', '']);
    allData.push([t('schedule-check-comparison.sections.missing-in-some'), '', '', '']);
    allData.push(['', '', '', '']);

    // Headers for partial section (same as complete section)
    allData.push([
      t('schedule-check.export.reference'),
      t('schedule-check.export.type'),
      t('schedule-check.export.drawings'),
      t('schedule-check.export.schematic'),
    ]);

    data.partialFindings.forEach((finding) => {
      const metadata = parseMetadata(finding);
      const reference = finding.content || '';

      const hasDrawing = metadata?.drawingMetadata;
      const hasSchematic = metadata?.schematicMetadata;

      let drawingFile: string;
      let schematicFile: string;

      if (!hasDrawing && hasSchematic) {
        // Missing in drawings, found in schematic
        drawingFile = t('schedule-check.export.missing');
        schematicFile = metadata?.schematicMetadata?.file_name || t('schedule-check.export.found');
      } else if (hasDrawing && !hasSchematic) {
        // Missing in schematic, found in drawings
        drawingFile = metadata?.drawingMetadata?.file_name || t('schedule-check.export.found');
        schematicFile = t('schedule-check.export.missing');
      } else {
        // Shouldn't happen, but handle gracefully
        drawingFile = hasDrawing
          ? metadata?.drawingMetadata?.file_name || t('schedule-check.export.found')
          : t('schedule-check.export.missing');
        schematicFile = hasSchematic
          ? metadata?.schematicMetadata?.file_name || t('schedule-check.export.found')
          : t('schedule-check.export.missing');
      }

      allData.push([
        reference,
        t('schedule-check-comparison.sections.missing-in-some'),
        drawingFile,
        schematicFile,
      ]);
    });
  }

  // Section 3: Missing in Both
  if (data.missingFindings.length > 0) {
    // Section header with empty cells to match column count
    allData.push(['', '', '', '']);
    allData.push([t('schedule-check-comparison.sections.missing-in-both'), '', '', '']);
    allData.push(['', '', '', '']);

    // Headers for missing section (same as other sections)
    allData.push([
      t('schedule-check.export.reference'),
      t('schedule-check.export.type'),
      t('schedule-check.export.drawings'),
      t('schedule-check.export.schematic'),
    ]);

    data.missingFindings.forEach((finding) => {
      const reference = finding.content || '';

      allData.push([
        reference,
        t('schedule-check-comparison.sections.missing-in-both'),
        t('schedule-check.export.missing'),
        t('schedule-check.export.missing'),
      ]);
    });
  }

  return {
    title,
    headers,
    data: allData,
  };
};
